import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';

const CartContext = createContext();

const CART_STORAGE_KEY = 'digitalMenuCart';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

// Helper function to generate unique cart item ID
const generateCartItemId = (item) => {
  const modifierIds = (item.selectedModifiers || [])
    .map(mod => mod.optionId)
    .sort()
    .join('-');
  return `${item.itemId}_${modifierIds}_${Date.now()}`;
};

// Helper function to compare modifiers between two items
const haveSameModifiers = (item1, item2) => {
  const mods1 = item1.selectedModifiers || [];
  const mods2 = item2.selectedModifiers || [];

  if (mods1.length !== mods2.length) return false;

  const ids1 = mods1.map(m => m.optionId).sort();
  const ids2 = mods2.map(m => m.optionId).sort();

  return ids1.every((id, index) => id === ids2[index]);
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = storage.get(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  // Persist cart to localStorage on every change
  useEffect(() => {
    try {
      storage.set(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // Silently fail on storage errors (quota exceeded, etc.)
    }
  }, [cart]);

  const confirmOrder = () => {
    setOrderConfirmed(true);
  };

  const addItem = (item, quantity = 1) => {
    // If order was confirmed, clear old items and start fresh
    if (orderConfirmed) {
      setOrderConfirmed(false);
    }

    setCart(prevCart => {
      // If order was confirmed, start with empty cart for the new item
      const baseCart = orderConfirmed ? [] : prevCart;

      // Find existing item with same itemId AND same modifiers
      const existingItem = baseCart.find(i =>
        i.itemId === item.itemId && haveSameModifiers(i, item)
      );

      if (existingItem) {
        // Increment quantity using cartItemId
        return baseCart.map(i =>
          i.cartItemId === existingItem.cartItemId
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      // Create new cart entry
      const unitPrice = item.unitPrice || item.price;
      const totalModifiersPrice = item.totalModifiersPrice || 0;
      const price = unitPrice + totalModifiersPrice;

      return [...baseCart, {
        ...item,
        cartItemId: generateCartItemId(item),
        unitPrice,
        totalModifiersPrice,
        price,
        quantity: item.quantity || quantity
      }];
    });
  };

  const removeItem = (cartItemId) => {
    setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId));
  };

  const restoreItem = (item) => {
    setCart(prevCart => [...prevCart, item]);
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeItem(cartItemId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = useCallback(() => {
    setCart([]);
    try {
      storage.remove(CART_STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }, []);

  const getSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate service charge using restaurant settings
  // settings parameter: { serviceCharge: number (percent), taxRate: number (percent) }
  const getServiceCharge = (settings = {}) => {
    const serviceChargePercent = settings.serviceCharge ?? 10; // Default 10%
    return getSubtotal() * (serviceChargePercent / 100);
  };

  // Calculate tax using restaurant settings
  const getTax = (settings = {}) => {
    const taxRatePercent = settings.taxRate ?? 21; // Default 21% IVA
    return (getSubtotal() + getServiceCharge(settings)) * (taxRatePercent / 100);
  };

  // Calculate total using restaurant settings
  const getTotal = (settings = {}) => {
    return getSubtotal() + getServiceCharge(settings) + getTax(settings);
  };

  const getItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        restoreItem,
        updateQuantity,
        clearCart,
        getSubtotal,
        getServiceCharge,
        getTax,
        getTotal,
        getItemCount,
        orderConfirmed,
        confirmOrder
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
